import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../constants";

interface NavigationOriginContextType {
  getOrigin: (currentPath: string) => string | undefined;
  setOrigin: (targetPath: string, originPath: string) => void;
  clearOrigin: (targetPath: string) => void;
}

const NavigationOriginContext =
  createContext<NavigationOriginContextType | null>(null);

// Pages that should track their origin (view pages that can be accessed from multiple places)
const TRACKABLE_PATTERNS = [
  /^\/treatment-plan\/\d+$/, // Treatment Plan View
  /^\/user-management\/\d+$/, // User Profile View
  /^\/patients\/[^/]+$/, // Patient Profile View
  /^\/sessions\/\d+$/, // Session Details View
  /^\/profile$/, // My Profile View
];

// Edit page patterns - when navigating from these, don't update origin
const EDIT_PATTERNS = [
  /^\/treatment-plan\/\d+\/edit$/,
  /^\/user-management\/\d+\/edit$/,
  /^\/profile\/edit$/,
];

// Get the base view path from an edit path
const getViewPathFromEdit = (editPath: string): string | null => {
  if (editPath.match(/^\/treatment-plan\/(\d+)\/edit$/)) {
    const id = editPath.match(/^\/treatment-plan\/(\d+)\/edit$/)?.[1];
    return id ? `/treatment-plan/${id}` : null;
  }
  if (editPath.match(/^\/user-management\/(\d+)\/edit$/)) {
    const id = editPath.match(/^\/user-management\/(\d+)\/edit$/)?.[1];
    return id ? `/user-management/${id}` : null;
  }
  if (editPath === "/profile/edit") {
    return "/profile";
  }
  return null;
};

// Check if path is an edit page
const isEditPage = (path: string): boolean => {
  return EDIT_PATTERNS.some((pattern) => pattern.test(path));
};

// Check if path is a trackable view page
const isTrackablePage = (path: string): boolean => {
  return TRACKABLE_PATTERNS.some((pattern) => pattern.test(path));
};

// Get default parent route (fallback)
const getDefaultParent = (pathname: string): string => {
  if (pathname.match(/^\/treatment-plan\/\d+$/)) {
    return ROUTES.SESSIONS;
  }
  if (pathname.match(/^\/sessions\/\d+$/)) {
    return ROUTES.SESSIONS;
  }
  if (pathname.match(/^\/patients\/[^/]+$/)) {
    return ROUTES.PATIENTS;
  }
  if (pathname.match(/^\/user-management\/\d+$/)) {
    return ROUTES.USER_MANAGEMENT;
  }
  if (pathname === "/profile") {
    return ROUTES.DASHBOARD;
  }
  return ROUTES.DASHBOARD;
};

export const NavigationOriginProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const [origins, setOrigins] = useState<Record<string, string>>({});
  const previousPathRef = useRef<string | null>(null);

  // Track navigation and set origins automatically
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    if (previousPath && currentPath !== previousPath) {
      // If navigating TO a trackable page
      if (isTrackablePage(currentPath)) {
        // Don't update origin if coming from its own edit page
        const viewPathFromEdit = previousPath
          ? getViewPathFromEdit(previousPath)
          : null;
        if (viewPathFromEdit !== currentPath) {
          // Coming from a different page - set origin
          // But don't set origin if coming from an edit page (that means we saved)
          if (!isEditPage(previousPath)) {
            // Don't update origin if we're navigating BACK to the page that was our origin
            // This prevents loops like: A -> B -> GoBack to A -> GoBack goes to B again
            setOrigins((prev) => {
              const previousPageOrigin = prev[previousPath];
              if (previousPageOrigin === currentPath) {
                // We're going back to our origin, don't update it
                return prev;
              }
              return {
                ...prev,
                [currentPath]: previousPath,
              };
            });
          }
        }
      }
    }

    previousPathRef.current = currentPath;
  }, [location.pathname]);

  const getOrigin = useCallback(
    (currentPath: string): string | undefined => {
      return origins[currentPath] || getDefaultParent(currentPath);
    },
    [origins],
  );

  const setOrigin = useCallback((targetPath: string, originPath: string) => {
    setOrigins((prev) => ({
      ...prev,
      [targetPath]: originPath,
    }));
  }, []);

  const clearOrigin = useCallback((targetPath: string) => {
    setOrigins((prev) => {
      const newOrigins = { ...prev };
      delete newOrigins[targetPath];
      return newOrigins;
    });
  }, []);

  return (
    <NavigationOriginContext.Provider
      value={{ getOrigin, setOrigin, clearOrigin }}
    >
      {children}
    </NavigationOriginContext.Provider>
  );
};

export const useNavigationOrigin = (): NavigationOriginContextType => {
  const context = useContext(NavigationOriginContext);
  if (!context) {
    throw new Error(
      "useNavigationOrigin must be used within NavigationOriginProvider",
    );
  }
  return context;
};
